import { Tag } from 'antd'
import { anchorBelongsToZone } from 'features/libra/comments/anchorMatching'
import { useReveal } from 'features/libra/comments/CommentsProvider'
import { DocumentDrawingControls } from 'features/libra/drawing/DocumentDrawingControls'
import { DocumentDrawingView } from 'features/libra/drawing/DocumentDrawingView'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { AiPrefillButton } from 'features/libra/stepComponents/AiPrefillButton'
import {
  AiPrefillGate,
  prefillLabel,
} from 'features/libra/stepComponents/AiPrefillControls'
import { useGongAgentAutofill } from 'features/libra/stepComponents/GongAgentAutofill'
import {
  buildDraftZones,
  parseZoneProposals,
} from 'features/libra/stepComponents/zoneAnalysis/aiSuggestedZones'
import {
  ZoneChangeGuard,
  ZoneParams,
  ZoneTabKey,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import {
  ZONE_CARD_TABS,
  ZonePrefillDerivationContext,
  ZoneTabAgentPermissions,
} from 'features/libra/stepComponents/zoneAnalysis/zoneTabAutofill'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useGeckoNotifications } from 'features/notifications'
import { Sparkles } from 'lucide-react'
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'
import { RolodexPermissionsValues } from 'types'

import { CropDocumentPanel } from './CropDocumentPanel'
import { useZoneEditorInteraction } from './useZoneEditorInteraction'
import { ZonesEditorRow } from './ZonesEditorRow'
import {
  Container,
  DrawingInstruction,
  EmptyStateContainer,
  EmptyStateDescription,
  EmptyStateTitle,
  EmptyZoneList,
  Label,
  LeftControls,
  RightControls,
  TopControls,
  ZonesHeader,
  ZonesList,
} from './zonesEditorSurfaceStyles'

export interface ZonesEditorSurfaceProps {
  zones: ZoneParams[]
  /** Embedded owners validate staged geometry against the saved snapshot. */
  mutationBaselineZones?: readonly ZoneParams[]
  onAddZone: (zone: ZoneParams) => void
  onChangeZone: (
    zoneId: string,
    updates: Partial<ZoneParams>,
    guard?: ZoneChangeGuard
  ) => void
  onRemoveZone: (zoneId: string) => void
  onConfirmProposal: (proposal: ZoneParams) => void
  onReplaceImageProposals: (imageId: string, proposals: ZoneParams[]) => void
  unitId: string
  agentSectionId: string
  zoneAgentPermissions: RolodexPermissionsValues[]
  visibleTabs?: ZoneTabKey[]
  zoneTabAgentPermissions?: ZoneTabAgentPermissions
  prefillDerivationContext?: ZonePrefillDerivationContext
  showCoverageBreakdown?: boolean
  readonly?: boolean
  onSaveZone?: (zoneId: string) => Promise<void>
  isZoneDirty?: (zoneId: string) => boolean
  isSaving?: boolean
  savingZoneId?: string
  getZoneSaveError?: (zoneId: string) => string | undefined
  isLoading: boolean
  drawingResetKey?: number
}

/** Edits zones without owning their persistence, so multiple workflow steps can reuse it. */
export const ZonesEditorSurface: FC<ZonesEditorSurfaceProps> = ({
  zones,
  mutationBaselineZones,
  onAddZone,
  onChangeZone,
  onRemoveZone,
  onConfirmProposal,
  onReplaceImageProposals,
  unitId,
  agentSectionId,
  zoneAgentPermissions,
  visibleTabs,
  zoneTabAgentPermissions,
  prefillDerivationContext,
  showCoverageBreakdown = true,
  readonly = false,
  onSaveZone,
  isZoneDirty,
  isSaving = false,
  savingZoneId,
  getZoneSaveError,
  isLoading,
  drawingResetKey,
}) => {
  const { errorNotification, infoNotification, successNotification } =
    useGeckoNotifications()
  const interaction = useZoneEditorInteraction({
    zones,
    mutationBaselineZones,
    onAddZone,
    onChangeZone,
    onRemoveZone,
    unitId,
    readonly,
    isLoading,
    drawingResetKey,
  })
  const {
    applyZoneOperation,
    boxRevision,
    drawing,
    drawingPixelPolicy,
    drawingReportsRef,
    drawingResetKeyRef,
    drawingTargetRef,
    handleDeleteZone,
    handleQueueBoxAppend,
    handleRedrawBox,
    handleRemoveRectangle,
    handleSelectBox,
    handleSelectZone,
    handleUpdateZone,
    imageNaturalSize,
    imageNaturalSizeRef,
    openDocument,
    readonlyRef,
    registerRow,
    replaceZonesFromAI,
    selectedBoxTarget,
    selectedDocument,
    selectedZoneId,
    setZoneSelection,
    zonesRef,
  } = interaction
  const effectiveZoneTabAgentPermissions = useMemo<ZoneTabAgentPermissions>(
    () =>
      zoneTabAgentPermissions ??
      (Object.fromEntries(
        ZONE_CARD_TABS.map((tab) => [tab, zoneAgentPermissions])
      ) as ZoneTabAgentPermissions),
    [zoneAgentPermissions, zoneTabAgentPermissions]
  )
  const documentNamesById = useMemo(
    () =>
      new Map(
        drawing.reports.map((doc) => [
          doc.id,
          doc.data.visibleName ||
            (typeof doc.raw.data.filename === 'string'
              ? doc.raw.data.filename
              : doc.id.substring(0, 8)),
        ])
      ),
    [drawing.reports]
  )

  // Following a link to a zone comment shows the zone's reference image,
  // the same as clicking the zone row.
  const stepId = useActiveStepId()
  useReveal((anchor) => {
    const zone = zones.find((z) => anchorBelongsToZone(anchor, stepId, z.id))
    if (zone) handleSelectZone(zone)
  })

  // A late AI response must use the image and dimensions captured when requested.
  const suggestTargetRef = useRef<{
    imageId: string
    sourceKey: string
    reportIdentity: string
    naturalSize: { width: number; height: number }
    drawingResetKey?: number
  }>()

  useLayoutEffect(
    () => () => {
      suggestTargetRef.current = undefined
    },
    [drawingResetKey, readonly]
  )

  const onZoneFieldValues = useCallback(
    (fieldValues: FieldValues, meta: { documentsUsed: string[] }) => {
      const target = suggestTargetRef.current
      if (
        readonlyRef.current ||
        !target ||
        target.drawingResetKey !== drawingResetKeyRef.current
      )
        return
      const report = drawingReportsRef.current.find(
        (item) => item.id === target.imageId
      )
      const reportIdentity = report
        ? JSON.stringify([
            report.id,
            report.generation,
            report.raw.data.filename,
          ])
        : undefined
      const currentDrawingTarget = drawingTargetRef.current
      const replacedBoundSource =
        currentDrawingTarget?.documentId === target.imageId &&
        JSON.stringify([
          currentDrawingTarget.documentId,
          currentDrawingTarget.url,
        ]) !== target.sourceKey
      if (target.reportIdentity !== reportIdentity || replacedBoundSource)
        return
      const proposals = parseZoneProposals(fieldValues)
      if (proposals.length === 0) {
        infoNotification('The AI did not find any zones on this image.')
        return
      }
      const { keptZones, newZones } = buildDraftZones({
        proposals,
        naturalSize: target.naturalSize,
        imageId: target.imageId,
        existingZones: zonesRef.current,
      })
      const nextZones = [...keptZones, ...newZones]
      // AI drafts have their own review lifetime. They do not change the
      // target identity of an existing box command or confirmation.
      replaceZonesFromAI(nextZones)
      onReplaceImageProposals(target.imageId, newZones)
      const docs = meta.documentsUsed
      const docsNote = docs.length
        ? ` Informed by ${docs.length} document${docs.length > 1 ? 's' : ''}.`
        : ''
      successNotification(
        `${proposals.length} AI zone${proposals.length > 1 ? 's' : ''} suggested — review and confirm, reject, or edit each.${docsNote}`
      )
    },
    [
      onReplaceImageProposals,
      replaceZonesFromAI,
      successNotification,
      infoNotification,
      drawingReportsRef,
      drawingResetKeyRef,
      drawingTargetRef,
      readonlyRef,
      zonesRef,
    ]
  )

  const {
    autofill: suggestZones,
    loading: suggesting,
    error: suggestError,
  } = useGongAgentAutofill({
    sectionId: agentSectionId,
    onFieldValues: onZoneFieldValues,
    inputs: useMemo(
      () =>
        selectedDocument
          ? { zone_reference_image_id: selectedDocument.id }
          : undefined,
      [selectedDocument]
    ),
  })

  useEffect(() => {
    if (suggestError) {
      errorNotification(`AI zone suggestion failed: ${suggestError}`)
    }
  }, [suggestError, errorNotification])

  const handleSuggestZones = useCallback(async () => {
    if (!selectedDocument) return
    const target = drawing.viewProps.target
    const report = drawing.reports.find(
      (item) => item.id === selectedDocument.id
    )
    if (
      !imageNaturalSize ||
      imageNaturalSize.documentId !== selectedDocument.id ||
      target?.documentId !== selectedDocument.id ||
      !target.url ||
      !report
    ) {
      infoNotification(
        'The reference image is still loading — try again in a moment.'
      )
      return
    }
    suggestTargetRef.current = {
      imageId: selectedDocument.id,
      sourceKey: JSON.stringify([target.documentId, target.url]),
      reportIdentity: JSON.stringify([
        report.id,
        report.generation,
        report.raw.data.filename,
      ]),
      naturalSize: {
        width: imageNaturalSize.width,
        height: imageNaturalSize.height,
      },
      drawingResetKey: drawingResetKeyRef.current,
    }
    await suggestZones()
  }, [
    selectedDocument,
    imageNaturalSize,
    suggestZones,
    infoNotification,
    drawing.viewProps.target,
    drawing.reports,
    drawingResetKeyRef,
  ])

  const handleConfirmZone = useCallback(
    (zoneId: string) => {
      if (readonlyRef.current) return
      const current = zonesRef.current
      const zone = current.find((item) => item.id === zoneId)
      if (!zone?.zone_reference_image_id || !zone.drawing_bounds?.length) {
        errorNotification(
          `${zone?.title ?? 'This zone'} needs a rectangle before it can be confirmed.`
        )
        return
      }
      const measured = imageNaturalSizeRef.current
      if (
        selectedDocument?.id !== zone.zone_reference_image_id ||
        measured?.documentId !== zone.zone_reference_image_id
      ) {
        setZoneSelection(zoneId, 0)
        openDocument(zone.zone_reference_image_id)
        infoNotification(
          `${zone.title} is still provisional. Confirm it again after its reference image loads.`
        )
        return
      }
      applyZoneOperation(
        current.map((item) =>
          item.id === zoneId ? { ...item, ai_suggested: false } : item
        ),
        () => onConfirmProposal(zone),
        [zoneId]
      )
    },
    [
      applyZoneOperation,
      errorNotification,
      infoNotification,
      onConfirmProposal,
      openDocument,
      selectedDocument,
      setZoneSelection,
      imageNaturalSizeRef,
      readonlyRef,
      zonesRef,
    ]
  )

  const zoneListSection = (
    <div key='zone-list'>
      <ZonesHeader>All Defined Zones ({zones.length})</ZonesHeader>
      {zones.length === 0 ? (
        <EmptyZoneList description='No zones defined yet. Create a zone reference image and draw zones on it.' />
      ) : (
        <ZonesList>
          {zones.map((zone) => (
            <ZonesEditorRow
              key={zone.id}
              zone={zone}
              sourceDocumentName={
                zone.zone_reference_image_id
                  ? documentNamesById.get(zone.zone_reference_image_id)
                  : undefined
              }
              selected={selectedZoneId === zone.id}
              selectedBoxIndex={
                selectedBoxTarget?.zoneId === zone.id
                  ? selectedBoxTarget.boxIndex
                  : undefined
              }
              revision={boxRevision}
              readonly={readonly}
              showCoverageBreakdown={showCoverageBreakdown}
              enableNiiCoverage={prefillDerivationContext !== undefined}
              visibleTabs={visibleTabs}
              zoneTabAgentPermissions={effectiveZoneTabAgentPermissions}
              prefillDerivationContext={prefillDerivationContext}
              isZoneDirty={isZoneDirty?.(zone.id)}
              isSaving={isSaving}
              savingZoneId={savingZoneId}
              saveError={getZoneSaveError?.(zone.id)}
              onRegisterRow={registerRow}
              onSelectZone={handleSelectZone}
              onSelectBox={handleSelectBox}
              onAddBox={handleQueueBoxAppend}
              onRedrawBox={handleRedrawBox}
              onRemoveRectangle={handleRemoveRectangle}
              onUpdateZone={handleUpdateZone}
              onDeleteZone={handleDeleteZone}
              onConfirmZone={handleConfirmZone}
              onSaveZone={onSaveZone}
            />
          ))}
        </ZonesList>
      )}
    </div>
  )

  const selectedDocumentName = selectedDocument
    ? selectedDocument.data.visibleName ||
      (selectedDocument.raw.data.filename as string | undefined) ||
      'this document'
    : undefined
  const surfaceContent = drawing.crop ? (
    <CropDocumentPanel {...drawing.crop} />
  ) : (
    <>
      {drawing.isReference && selectedDocument ? (
        <>
          <TopControls>
            <LeftControls>
              <Label>
                Zone Reference Image
                <Tag color='blue'>
                  {selectedDocument.data.visibleName || 'Reference Image'}
                </Tag>
              </Label>
            </LeftControls>

            {!readonly && (
              <RightControls>
                <AiPrefillGate permissions={zoneAgentPermissions}>
                  <AiPrefillButton
                    plain
                    icon={<Sparkles size={16} />}
                    onClick={handleSuggestZones}
                    loading={suggesting}
                    disabled={drawing.isDrawing}>
                    {prefillLabel({ loading: suggesting, hasRun: false })}
                  </AiPrefillButton>
                </AiPrefillGate>
                <DocumentDrawingControls
                  drawing={drawing}
                  drawLabel={drawing.commandContext ? 'Add Box' : 'Add Zone'}
                />
              </RightControls>
            )}
          </TopControls>

          {drawing.isDrawing && (
            <DrawingInstruction>
              <EmptyStateDescription>
                {drawing.commandContext
                  ? 'Draw a rectangle on the image to add it to the selected zone.'
                  : 'Draw a rectangle on the image to the left to define a zone boundary.'}
              </EmptyStateDescription>
            </DrawingInstruction>
          )}
        </>
      ) : selectedDocument ? (
        !readonly && (
          <EmptyStateContainer>
            <EmptyStateTitle>Create Zone Reference Image</EmptyStateTitle>
            <EmptyStateDescription>
              Crop a region from the currently selected document (
              {selectedDocumentName}) to create a zone reference image.
            </EmptyStateDescription>
            <DocumentDrawingControls
              drawing={drawing}
              cropLabel='Crop Current Document'
            />
          </EmptyStateContainer>
        )
      ) : (
        <EmptyStateContainer>
          <EmptyStateTitle>No Document Selected</EmptyStateTitle>
          <EmptyStateDescription>
            Select a construction drawing or P&amp;ID from the left sidebar to
            create a zone reference image, or select an existing zone reference
            image to define zones.
          </EmptyStateDescription>
        </EmptyStateContainer>
      )}

      {zoneListSection}
    </>
  )

  return (
    <Container>
      <DocumentDrawingView
        {...drawing.viewProps}
        pixelPolicy={drawingPixelPolicy}
      />
      {surfaceContent}
    </Container>
  )
}
